"use client"

import { Stack, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import StatsGrid from "@/components/StatsGrid"
import { type StatsBlock as StatsBlockType } from "@/types/database"
import { type PostStat } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface StatsBlockProps extends ContentBlockTitleNumberProps {
  block: StatsBlockType
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value
  }

  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

const getString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()

    return trimmedValue.length > 0 ? trimmedValue : undefined
  }

  if (typeof value === "number") {
    return String(value)
  }

  return undefined
}

const getPositiveInteger = (value: unknown): number | undefined => {
  const parsedValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value.trim())
        : Number.NaN

  if (!Number.isFinite(parsedValue)) {
    return undefined
  }

  const integerValue = Math.floor(parsedValue)

  return integerValue > 0 ? integerValue : undefined
}

const getBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value
  }

  if (typeof value === "number") {
    return value === 1
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase()

    if (["1", "true", "yes"].includes(normalizedValue)) {
      return true
    }

    if (["0", "false", "no"].includes(normalizedValue)) {
      return false
    }
  }

  return undefined
}

const normalizeStat = (item: unknown): PostStat | null => {
  const parsedItem = parseJsonValue(item)

  if (!isRecord(parsedItem)) {
    return null
  }

  const stat: PostStat = {
    stat_title:
      getString(parsedItem.stat_title) ??
      getString(parsedItem.title) ??
      getString(parsedItem.label),
    stat_value:
      getString(parsedItem.stat_value) ??
      getString(parsedItem.value) ??
      getString(parsedItem.metric),
    stats_description:
      getString(parsedItem.stats_description) ??
      getString(parsedItem.description) ??
      getString(parsedItem.help_text),
    stats_down: getBoolean(parsedItem.stats_down ?? parsedItem.down),
    stats_leading_icon:
      getString(parsedItem.stats_leading_icon) ??
      getString(parsedItem.leading_icon),
    stats_trailing_icon:
      getString(parsedItem.stats_trailing_icon) ??
      getString(parsedItem.trailing_icon),
    stats_up: getBoolean(parsedItem.stats_up ?? parsedItem.up),
  }

  if (!stat.stat_title && !stat.stat_value && !stat.stats_description) {
    return null
  }

  return stat
}

const normalizeStats = (value: unknown): PostStat[] => {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.reduce<PostStat[]>((stats, item) => {
      const stat = normalizeStat(item)

      if (stat !== null) {
        stats.push(stat)
      }

      return stats
    }, [])
  }

  if (isRecord(parsedValue)) {
    const nestedItems =
      parsedValue.stats ??
      parsedValue.items ??
      parsedValue.values ??
      parsedValue.metrics

    if (nestedItems) {
      return normalizeStats(nestedItems)
    }
  }

  return []
}

const StatsBlock: FC<StatsBlockProps> = ({ block, titleNumber }) => {
  const {
    after_content,
    columns_number,
    darker_bg,
    description,
    previous_content,
    stats,
    title,
    two_columns_stats,
  } = block
  const trimmedTitle = title?.trim()
  const trimmedDescription = description?.trim()
  const trimmedPreviousContent = previous_content?.trim()
  const trimmedAfterContent = after_content?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription = Boolean(trimmedDescription)
  const hasPreviousContent = Boolean(trimmedPreviousContent)
  const hasAfterContent = Boolean(trimmedAfterContent)
  const normalizedStats = normalizeStats(stats)
  const configuredColumns = getPositiveInteger(columns_number)
  const statsColumns =
    configuredColumns === undefined
      ? undefined
      : {
          base: 1,
          sm: Math.min(Math.max(normalizedStats.length, 1), configuredColumns),
        }
  const statsGridColumnProps =
    statsColumns === undefined ? {} : { columns: statsColumns }

  if (
    !hasTitle &&
    !hasDescription &&
    !hasPreviousContent &&
    !hasAfterContent &&
    normalizedStats.length === 0
  ) {
    return null
  }

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="stats-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns_stats ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_stats ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: two_columns_stats ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(Boolean(two_columns_stats))}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={trimmedDescription}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: two_columns_stats ? "70%" : "100%",
        }}
        pl={!hasTitle && two_columns_stats ? { base: 0, lg: 12 } : 0}
        w="full"
      >
        <BlurFade flexDirection="column" gap={8} inView w="full">
          {hasPreviousContent && (
            <MarkdownParser
              fontSize={{ base: "md", lg: "md" }}
              markdown={trimmedPreviousContent}
              pProps={{ color: "fg.muted" }}
            />
          )}
          {normalizedStats.length > 0 && (
            <StatsGrid
              {...statsGridColumnProps}
              ariaLabel={trimmedTitle ?? "Stats"}
              border={"1px solid {colors.border}"}
              borderRadius="lg"
              stats={normalizedStats}
            />
          )}
          {hasAfterContent && (
            <MarkdownParser
              fontSize={{ base: "md", lg: "md" }}
              markdown={trimmedAfterContent}
              pProps={{ color: "fg.muted" }}
            />
          )}
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default StatsBlock
